import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";

import { env } from "@/configs/env";
import { useAuthStore } from "@/store/auth";
import type { ApiEnvelope, AuthSession, PageMeta, Paginated } from "@/types/api";

import { REFRESHABLE_CODES, parseApiError } from "./errors";

/**
 * API mijozi. Refresh token backend o'rnatgan HttpOnly cookie'da
 * (`aimly_rt`, `Path=/api/v1/auth`), shuning uchun `withCredentials`.
 */
export const api = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: true,
  timeout: 20_000,
});

let inflight: Promise<AuthSession> | null = null;

/**
 * Bitta vaqtda bitta refresh: parallel so'rovlar har biri o'z refresh'ini
 * yuborsa, rotatsiya buni "qayta ishlatish" deb butun sessiyani yopardi.
 */
export function refreshSession(): Promise<AuthSession> {
  inflight ??= axios
    .post<ApiEnvelope<AuthSession>>(`${env.apiBaseUrl}/auth/refresh`, null, {
      withCredentials: true,
    })
    .then(res => {
      useAuthStore.getState().setSession(res.data.data);
      return res.data.data;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

api.interceptors.request.use(config => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

type RetryConfig = InternalAxiosRequestConfig & { _retried?: boolean };

api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined;
    const { code, status } = parseApiError(error);

    if (status === 401 && config && !config._retried && REFRESHABLE_CODES.has(code)) {
      config._retried = true;
      try {
        const session = await refreshSession();
        config.headers.Authorization = `Bearer ${session.accessToken}`;
        return api.request(config);
      } catch {
        useAuthStore.getState().clear();
      }
    } else if (status === 401) {
      // Tiklab bo'lmaydigan 401 (token yaroqsiz, sessiya yopilgan) — qayta kirish.
      useAuthStore.getState().clear();
    }
    throw error;
  },
);

/** Konvertdan `data` ni oladi. */
export async function unwrap<T>(promise: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  return (await promise).data.data;
}

/** Sahifalangan konvert: `data` + `meta`. */
export async function unwrapPage<T>(
  promise: Promise<{ data: { data: T[]; meta: PageMeta } }>,
): Promise<Paginated<T>> {
  const body = (await promise).data;
  return { data: body.data, meta: body.meta };
}
